import { type PartialDeep } from 'type-fest';

import { type CanvasElementFrom, type PropsOf } from '@blaze-runtime/engine-domain';

import {
  getSourceProps,
  getSourceTransforms,
} from '../../canvas-elements/element-model/transformations';

type TextFieldElement = CanvasElementFrom<'textField'>;
type TextFieldProps = PartialDeep<PropsOf<'textField'>>;

function transformDatetimeInputToTextField(
  source: CanvasElementFrom<'datetimeInput'>,
): TextFieldElement {
  const props: PartialDeep<PropsOf<'datetimeInput'>> = source.props || {};
  const propsT = getSourceTransforms<'datetimeInput', 'textField'>(source, 'textField');
  const newProps: TextFieldProps = {
    content: {
      title: {
        enabled: props.content?.title?.enabled,
        label: props.content?.title?.label,
        position: props.content?.title?.position,
        align: {
          ...propsT?.content?.title?.align,
          ...(props?.content?.title?.align ? { horizontal: props.content.title.align } : undefined),
        },
        width: props.content?.title?.width,
        icon: props.content?.title?.icon,
        iconPosition: props.content?.title?.iconPosition,
        tooltip: props.content?.title?.tooltip,
      },
      input: {
        placeholder: propsT?.content?.input?.placeholder,
        format:
          (propsT?.content?.input?.format ?? props.content?.time?.enabled) ? 'datetime' : 'date',
        precision: propsT?.content?.input?.precision,
        currency: propsT?.content?.input?.currency,
      },
      container: {
        overflow: propsT?.content?.container?.overflow,
      },
    },
    style: {
      fontSize: props.style?.fontSize,
      fontFamily: props.style?.fontFamily,
      lineHeight: props.style?.lineHeight,
      general: {
        title: props.style?.general?.title,
        accent: props.style?.general?.accent,
      },
      inputFont: {
        style: propsT?.style?.inputFont?.style,
        fontFamily: propsT?.style?.inputFont?.fontFamily,
        fontSize: propsT?.style?.inputFont?.fontSize,
        lineHeight: propsT?.style?.inputFont?.lineHeight,
      },
      input: {
        value: props.style?.input?.value,
        fill: props.style?.input?.fill,
        border: props.style?.input?.border,
        radius: props.style?.input?.radius,
      },
    },
    transforms: {
      datetimeInput: {
        content: {
          title: {
            align: props.content?.title?.align,
          },
          date: {
            enabled: props.content?.date?.enabled,
            format: props.content?.date?.format,
            min: props.content?.date?.min,
            max: props.content?.date?.max,
            defaultValue: props.content?.date?.defaultValue,
          },
          time: {
            enabled: props.content?.time?.enabled,
            format: props.content?.time?.format,
            min: props.content?.time?.min,
            max: props.content?.time?.max,
          },
        },
      } as PartialDeep<PropsOf<'datetimeInput'>>,
    },
  };

  // TODO: fix type
  return { props: newProps } as any;
}

function transformDropdownToTextField(source: CanvasElementFrom<'dropdown'>): TextFieldElement {
  const props = getSourceProps(source);
  const propsT = getSourceTransforms<'dropdown', 'textField'>(source, 'textField');
  const newProps: TextFieldProps = {
    content: {
      title: {
        enabled: props.content?.title?.enabled,
        label: props.content?.title?.label,
        position: props.content?.title?.position,
        align: {
          horizontal: props.content?.title?.align,
        },
        width: props.content?.title?.width,
        icon: props.content?.title?.icon,
        iconPosition: props.content?.title?.iconPosition,
        tooltip: props.content?.title?.tooltip,
      },
      input: {
        placeholder: props.content?.menu?.placeholder,
        format: propsT?.content?.input?.format,
        precision: propsT?.content?.input?.precision,
        currency: propsT?.content?.input?.currency,
      },
      container: {
        overflow: propsT?.content?.container?.overflow,
      },
    },
    style: {
      fontSize: props.style?.fontSize,
      fontFamily: props.style?.fontFamily,
      lineHeight: props.style?.lineHeight,
      general: {
        title: props.style?.general?.title,
        accent: props.style?.general?.accent,
      },
      input: {
        value: props.style?.input?.color,
        fill: props.style?.input?.fill,
        border: props.style?.input?.border,
        radius: props.style?.input?.radius,
      },
    },
    transforms: {
      dropdown: {
        content: {
          data: {
            defaultValue: props.content?.data?.defaultValue,
          },
          menu: {
            values: props.content?.menu?.values,
            preselect: props.content?.menu?.preselect,
            prefix: props.content?.menu?.prefix,
            suffix: props.content?.menu?.suffix,
          },
        },
        style: {
          list: {
            value: props.style?.list?.value,
            background: props.style?.list?.background,
            border: props.style?.list?.border,
            radius: props.style?.list?.radius,
          },
        },
      } as PartialDeep<PropsOf<'dropdown'>>,
    },
  };

  return { props: newProps } as TextFieldElement;
}

function transformImageToTextField(source: CanvasElementFrom<'image'>): TextFieldElement {
  const props = getSourceProps(source);
  const propsT = getSourceTransforms<'image', 'textField'>(source, 'textField');
  const newProps: TextFieldProps = {
    content: {
      title: {
        enabled: props.content?.title?.enabled,
        label: props.content?.title?.label,
        position: props.content?.title?.position,
        align: {
          ...propsT?.content?.title?.align,
          horizontal: props.content?.title?.align,
        },
        width: props.content?.title?.width,
        icon: props.content?.title?.icon,
        iconPosition: props.content?.title?.iconPosition,
        tooltip: props.content?.title?.tooltip,
      },
      input: propsT?.content?.input,
      container: {
        overflow: propsT?.content?.container?.overflow,
      },
    },
    style: {
      fontSize: props.style?.fontSize,
      fontFamily: props.style?.fontFamily,
      lineHeight: props.style?.lineHeight,
      general: {
        title: props.style?.general?.title,
        accent: propsT?.style?.general?.accent,
        // Stashed transforms are not covered by element migrations, so a stash written
        // before BL-10389 still stores external spacing as offset.padding
        offset: {
          margin: propsT?.style?.general?.offset?.margin ?? propsT?.style?.general?.offset?.padding,
        },
      },
      inputFont: propsT?.style?.inputFont,
      input: {
        ...propsT?.style?.input,
        border: props.style?.outline?.color,
        radius: props.style?.outline?.radius,
      },
    },
    transforms: {
      image: {
        content: {
          imageSource: props.content?.imageSource,
          options: props.content?.options,
          link: props.content?.link,
        },
        style: {
          image: props.style?.image,
          outline: {
            width: props.style?.outline?.width,
          },
        },
      } as PartialDeep<PropsOf<'image'>>,
    },
  };

  return { props: newProps } as TextFieldElement;
}

function transformPdfViewerToTextField(source: CanvasElementFrom<'pdfViewer'>): TextFieldElement {
  const props: PartialDeep<PropsOf<'pdfViewer'>> = source.props || {};
  const newProps: TextFieldProps = {
    content: {
      title: {
        enabled: props.content?.title?.enabled,
        label: props.content?.title?.label,
        align: { horizontal: props.content?.title?.align },
        icon: props.content?.title?.icon,
        iconPosition: props.content?.title?.iconPosition,
      },
    },
    style: {
      general: {
        title: props.style?.label?.color,
      },
      input: {
        border: props.style?.border?.outline,
        radius: props.style?.border?.radius,
      },
    },
  };

  // TODO: fix type
  return { props: newProps } as any;
}

function transformTagsToTextField(source: CanvasElementFrom<'tags'>): TextFieldElement {
  const props: PartialDeep<PropsOf<'tags'>> = source.props || {};
  const newProps: TextFieldProps = {
    content: {
      title: {
        enabled: props.content?.title?.enabled,
        label: props.content?.title?.label,
        position: props.content?.title?.position,
        align: { horizontal: props.content?.title?.align },
        width: props.content?.title?.width,
        icon: props.content?.title?.icon,
        iconPosition: props.content?.title?.iconPosition,
        tooltip: props.content?.title?.tooltip,
      },
      input: {
        placeholder: props.content?.tags?.placeholder,
        format: undefined, // no mapping
      },
    },
    style: {
      fontSize: props.style?.fontSize,
      fontFamily: props.style?.fontFamily,
      lineHeight: props.style?.lineHeight,
      general: {
        title: props.style?.general?.title,
        accent: props.style?.general?.accent,
      },
      input: {
        value: props.style?.input?.color,
        fill: props.style?.input?.fill,
        border: props.style?.input?.border,
        radius: props.style?.input?.radius,
      },
    },
  };

  // TODO: fix type
  return { props: newProps } as any;
}

function transformTextInputToTextField(source: CanvasElementFrom<'textInput'>): TextFieldElement {
  const props: PartialDeep<PropsOf<'textInput'>> = source.props || {};
  const newProps: TextFieldProps = {
    content: {
      title: {
        enabled: props.content?.title?.enabled,
        label: props.content?.title?.label,
        position: props.content?.title?.position,
        align: props.content?.title?.align,
        width: props.content?.title?.width,
        icon: props.content?.title?.icon,
        iconPosition: props.content?.title?.iconPosition,
        tooltip: props.content?.title?.tooltip,
      },
      input: {
        placeholder: props.content?.input?.placeholder,
        format: props.content?.input?.format,
        precision: props.content?.input?.precision,
        currency: props.content?.input?.currency,
      },
      container: {
        overflow: props?.content?.container?.overflow,
      },
    },
    style: {
      fontSize: props.style?.fontSize,
      fontFamily: props.style?.fontFamily,
      lineHeight: props.style?.lineHeight,
      general: {
        title: props.style?.general?.title,
        accent: props.style?.general?.accent,
      },
      inputFont: {
        style: props.style?.inputFont?.style,
        fontFamily: props.style?.inputFont?.fontFamily,
        fontSize: props.style?.inputFont?.fontSize,
        lineHeight: props.style?.inputFont?.lineHeight,
      },
      input: {
        value: props.style?.input?.value,
        fill: props.style?.input?.fill,
        border: props.style?.input?.border,
        radius: props.style?.input?.radius,
      },
    },
  };

  // TODO: fix type
  return { props: newProps } as any;
}

export const transformToTextField = {
  transformDatetimeInputToTextField,
  transformDropdownToTextField,
  transformImageToTextField,
  transformPdfViewerToTextField,
  transformTagsToTextField,
  transformTextInputToTextField,
};
